import {
  CalendarDays,
  ChartPie,
  House,
  Receipt,
  Settings,
  ShoppingCart,
  SlidersHorizontal,
  Wallet,
  type LucideIcon,
} from "lucide-react";

export type PageId =
  | "today"
  | "calendar"
  | "bills"
  | "shopping"
  | "finance-dashboard"
  | "finance-config"
  | "finance-budget"
  | "settings";

export interface NavItem {
  /** Absent on group headers, which are not pages themselves. */
  id?: PageId;
  label: string;
  icon: LucideIcon;
  /** Present on group headers (Finance); the children are the real pages. */
  children?: NavItem[];
  /** Reached by drilling in rather than from the nav, so not rendered in it. */
  hidden?: boolean;
}

export const NAV_ITEMS: NavItem[] = [
  { id: "today", label: "Today", icon: House },
  { id: "calendar", label: "Calendar", icon: CalendarDays },
  { id: "bills", label: "Bills", icon: Receipt },
  { id: "shopping", label: "Shopping", icon: ShoppingCart },
  {
    label: "Finance",
    icon: Wallet,
    children: [
      { id: "finance-dashboard", label: "Dashboard", icon: ChartPie },
      { id: "finance-config", label: "Configuration", icon: SlidersHorizontal },
      // Opened from a budget in Configuration, not from the nav itself.
      { id: "finance-budget", label: "Budget", icon: Wallet, hidden: true },
    ],
  },
  { id: "settings", label: "Settings", icon: Settings },
];

/** The page a group header opens when tapped (its first child). */
export function groupLanding(item: NavItem): PageId | undefined {
  return item.id ?? item.children?.[0]?.id;
}

/** Flat list of every real page, for label lookups. */
export const ALL_PAGES: NavItem[] = NAV_ITEMS.flatMap((item) => (item.children ? item.children : [item]));

/** The group a page belongs to, if any — used to title the header and expand the sidebar. */
export function groupOf(page: PageId): NavItem | undefined {
  return NAV_ITEMS.find((item) => item.children?.some((c) => c.id === page));
}

// ----- Routing --------------------------------------------------------------

/** A page, plus the id when the page addresses one thing (a budget). */
export interface Route {
  page: PageId;
  param?: string;
}

const PATHS: Record<PageId, string> = {
  today: "/",
  calendar: "/calendar",
  bills: "/bills",
  shopping: "/shopping",
  "finance-dashboard": "/finance",
  "finance-config": "/finance/configuration",
  "finance-budget": "/finance/budgets",
  settings: "/settings",
};

const BUDGET_PREFIX = "/finance/budgets/";

/** The URL for a page, so every view can be linked to and reloaded. */
export function pathFor(page: PageId, param?: string): string {
  const base = PATHS[page];
  return param ? `${base}/${param}` : base;
}

/** The page a URL addresses; anything unrecognised falls back to Today. */
export function parsePath(pathname: string): Route {
  const clean = pathname.replace(/\/+$/, "") || "/";

  if (clean.startsWith(BUDGET_PREFIX)) {
    const param = clean.slice(BUDGET_PREFIX.length);
    return param ? { page: "finance-budget", param } : { page: "finance-budget" };
  }

  const hit = (Object.entries(PATHS) as [PageId, string][]).find(([, p]) => p === clean);
  return { page: hit ? hit[0] : "today" };
}
